// @ts-expect-error: It will be removed during transition to TypeScript
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import { ApolloProvider } from '@vue/apollo-option'

import featureNames from '@/lib/featureNames'
import { needsReacceptance } from '@/modules/legalConsents/needsReacceptance'
import { getInstance } from '@/plugins/feature-config'
import LEGAL_CONSENTS from '@/queries/LegalConsents.gql'

import { getInstance as getAuthInstance } from '../auth/index'
import { authGuard } from '../guards/auth'

/**
 * Checks if the current user is responsible for legal-consents acceptance.
 * If so and a required, pre-onboarding-context item is unaccepted or stale,
 * forces the user to the legal-consents page before using the app.
 *
 * This runs before onboarding as well, so consents must be accepted before
 * onboarding can start.
 *
 * @param {ApolloProvider} apolloProvider apollo instance
 * @returns {import('vue-router').NavigationGuard}
 * @throws {Error}
 */
export function getLegalConsentsGuard(apolloProvider) {
  return (to, from, next) => {
    authGuard(async () => {
      try {
        const auth = getAuthInstance()

        if (!auth.isAuthenticated) {
          next()
          return
        }

        // Legal pages that have to be visible even if legal consents haven't been accepted.
        // 'licenseAgreement' is included to avoid an infinite redirect loop with
        // licenseAgreementGuard if that (currently dormant) feature is ever active
        // at the same time as legalConsents, matching avvGuard's own publicPages.
        const publicPages = [
          'licenseAgreement',
          'privacy',
          'avv',
          'legal',
          'company-creation',
          'logout',
          'registration',
        ]
        if (publicPages.includes(to.name)) {
          next()
          return
        }

        const featureConfig = getInstance()
        await featureConfig.loadAllFeaturesForCompany(auth.user.companyId, {
          apolloProvider,
          reload: false,
        })
        const legalConsentsFeature = featureConfig.featuresBySlug[featureNames.LEGAL_CONSENTS]

        if (legalConsentsFeature?.isActive) {
          if (to.name === 'legalConsents') {
            next()
            return
          }

          const preOnboardingItems = (legalConsentsFeature.config.items || []).filter((item) =>
            item.context.includes('pre-onboarding')
          )

          const apolloClient = apolloProvider.defaultClient
          const {
            data: { company },
          } = await apolloClient.query({
            query: LEGAL_CONSENTS,
            variables: {
              id: auth.user.companyId,
            },
          })

          if (!company) {
            throw new Error('Company not found')
          }

          if (needsReacceptance(company.legalConsents || [], preOnboardingItems)) {
            next({ name: 'legalConsents', replace: true })
            return
          }
        } else if (to.name === 'legalConsents') {
          next({ name: 'dashboard', replace: true })
          return
        }

        next()
      } catch (error) {
        console.error('Legal consents guard error:', error)
        next(false)
      }
    })
  }
}
